import { FunctionComponent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown } from "@/components/Dropdown/Dropdown";
import { useGetAiModelsQuery } from "@/pages/Private/redux/media/media.api";
import { AiModelUseCase } from "@/pages/Private/redux/media/media.schema";
import { VideoWizard } from "@/pages/Private/redux/media/videoWizard.schema";

import { stepOf } from "./wizard.utils";

interface ModelSelectRowProps {
	wizard: VideoWizard;
	step: number;
	useCase: AiModelUseCase;
	disabled?: boolean;
	onSelectModel: (aiModelId: number) => void;
}

/**
 * The per-step model picker with its estimate and actual badges — the mockup's
 * `modelRowHtml`. The estimate is the server's figure for this step, so switching model
 * changes the price by changing the registry row the server prices against, not by any
 * arithmetic here.
 */
export const ModelSelectRow: FunctionComponent<ModelSelectRowProps> = ({
	wizard,
	step,
	useCase,
	disabled,
	onSelectModel,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	const { data: models } = useGetAiModelsQuery({ useCase });
	const state = stepOf(wizard, step);

	const options = useMemo(
		() =>
			// The model's name, not its price. What a run costs is the rail's to say, and
			// quoting a unit price here invited the author to do the arithmetic themselves.
			(models ?? []).map((model) => ({ id: model.id, title: model.displayName })),
		[models]
	);

	const selected = useMemo(
		() =>
			options.find((option) => option.id === state?.aiModelId) ??
			options.find((option) => option.id === (models ?? []).find((m) => m.isDefault)?.id),
		[options, state?.aiModelId, models]
	);

	return (
		<div className="mb-4 flex flex-wrap items-end justify-between gap-3 rounded-card border border-border-soft bg-surface px-4 py-3">
			<div className="min-w-[260px] flex-1">
				{options.length > 1 ? (
					<Dropdown
						data={options}
						disabled={disabled}
						// Only on a real change — see the note in Step1Storyboard. This row is on
						// every step, so an unguarded handler loops the whole wizard.
						handleSelect={(item) =>
							Number(item.id) !== selected?.id && onSelectModel(Number(item.id))
						}
						label={ts("modelRow.label")}
						value={selected}
					/>
				) : (
					// One registered model is a fact, not a decision — no menu with a single
					// item. The name still shows: which pipeline runs is worth knowing.
					<div>
						<span className="block text-sm font-bold text-ink">{ts("modelRow.label")}</span>
						<span className="mt-1 block text-sm text-subtle">
							{selected?.title ?? options[0]?.title ?? "—"}
						</span>
					</div>
				)}
			</div>
		</div>
	);
};
